import { FontAwesome5, Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import type { LucideIcon } from "lucide-react-native";
import {
  Apple,
  Baby,
  Bean,
  CalendarDays,
  CheckCircle,
  CircleCheck,
  CircleSlash,
  Clock,
  Coffee,
  Cookie,
  Croissant,
  Egg,
  Fish,
  Heart,
  HeartPulse,
  Home,
  Leaf,
  Lightbulb,
  Milk,
  Moon,
  Package,
  Pizza,
  Refrigerator,
  Salad,
  Sandwich,
  Shell,
  Shield,
  Smile,
  Sparkles,
  Sprout,
  Star,
  Sun,
  Sunrise,
  ThumbsUp,
  Timer,
  TreePine,
  Users,
  Utensils,
  UtensilsCrossed,
  Vegan,
  Waves,
  Wheat,
  Zap,
} from "lucide-react-native";
import { Box, Button, HStack, Icon, Image, Pressable, ScrollView, Text, useTheme, VStack } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { useWindowDimensions } from "react-native";
import { useSelector } from "react-redux";

import ScreenContainer from "../../components/ScreenContainer";
import { Scale, VerticalScale } from "../../constants";
import { getOrganisation } from "../../helpers/userHelpers";
import { userSelector } from "../../slices/userSlice";

export interface Option {
  label: string;
  description: string;
  value: string;
  icon?: string;
  iconType?: string;
  testID?: string;
}

type IconProps = {
  name: string;
  type: string; // Optional, if not provided, it's an emoji
};

type SetSelectedOptionsFunctionType = (selectedOptions: string[]) => void;

interface SelectionScreenProps {
  title: string;
  subtitle: string;
  options: Option[];
  multipleSelection?: boolean;
  selectedOptions: string[];
  setSelectedOptions: SetSelectedOptionsFunctionType;
  icon?: IconProps;
  onPressNext: () => void;
  onPressBack?: () => void;
  errorMessage?: string | string[];
  submitButtonLoading?: boolean;
  nextButtonDisabled?: boolean;
  nextButtonTestIdPostfix?: string;
}

const DEFAULT_ICON: IconProps = { name: "robot", type: "material-community" };

const SelectionScreen: React.FC<SelectionScreenProps> = ({
  title,
  subtitle,
  options,
  multipleSelection = false,
  selectedOptions,
  setSelectedOptions,
  icon = DEFAULT_ICON,
  onPressNext,
  onPressBack,
  errorMessage,
  submitButtonLoading = false,
  nextButtonDisabled = false,
  nextButtonTestIdPostfix,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const theme = useTheme();

  // Theme-aware shadow values
  const NO_SHADOW = "none"; // No shadow for unselected state (clean, flat design)
  const SELECTED_SHADOW = "1"; // Very subtle shadow for selected state

  const user = useSelector(userSelector);
  const organisation = user ? getOrganisation(user) : undefined;
  const logoUri = organisation?.logo;

  const isDesktop = width >= 1024;

  const toggleSelection = (option: string): void => {
    if (multipleSelection) {
      const newSelectedOptions = selectedOptions.includes(option)
        ? selectedOptions.filter((item) => item !== option)
        : [...selectedOptions, option];

      setSelectedOptions(newSelectedOptions);
    } else {
      setSelectedOptions([option]);
    }
  };

  const lucideIconMap: Record<string, LucideIcon> = {
    Apple,
    Baby,
    Bean,
    CalendarDays,
    CheckCircle,
    CircleCheck,
    CircleSlash,
    Clock,
    Coffee,
    Cookie,
    Croissant,
    Egg,
    Fish,
    Heart,
    HeartPulse,
    Home,
    Leaf,
    Lightbulb,
    Milk,
    Moon,
    Package,
    Pizza,
    Refrigerator,
    Salad,
    Sandwich,
    Shell,
    Shield,
    Smile,
    Sparkles,
    Sprout,
    Star,
    Sun,
    Sunrise,
    ThumbsUp,
    Timer,
    TreePine,
    Users,
    Utensils,
    UtensilsCrossed,
    Vegan,
    Waves,
    Wheat,
    Zap,
  };

  const getOptionIconComponent = (option: Option, isSelected: boolean): JSX.Element => {
    if (!option.iconType) {
      return <Text fontSize="24">{option.icon}</Text>;
    }

    if (option.iconType === "lucide" && option.icon) {
      const LucideIcon = lucideIconMap[option.icon];
      if (LucideIcon) {
        return <LucideIcon size={24} color={isSelected ? theme.colors.primary[50] : theme.colors.function[30]} />;
      }
    }

    let IconComponent;
    if (option.iconType === "font-awesome-5") {
      IconComponent = FontAwesome5;
    } else if (option.iconType === "ionicon") {
      IconComponent = Ionicons;
    } else {
      IconComponent = MaterialCommunityIcons;
    }

    return <Icon as={IconComponent} name={option.icon} size="6" color={isSelected ? "primary.50" : "function.30"} />;
  };

  return (
    <ScreenContainer safeAreaMode="full">
      <Box flex={1}>
        <VStack flex={1}>
          {/* Header */}
          <HStack alignItems="center" p={4} px={isDesktop ? "20%" : 4}>
            {onPressBack ? (
              <Pressable
                onPress={onPressBack}
                testID={nextButtonTestIdPostfix ? `back-button-${nextButtonTestIdPostfix}` : "back-button"}
              >
                <Icon as={Ionicons} name="arrow-back" size="6" color="function.50" />
              </Pressable>
            ) : null}
          </HStack>

          {/* Content */}
          <ScrollView flex={1}>
            <VStack flex={1} alignItems="center" p={4} px={isDesktop ? "20%" : 4} space={4}>
              {/* Logo or Icon */}
              {logoUri ? (
                <Image
                  source={{ uri: logoUri }}
                  style={{ width: Scale(160), height: VerticalScale(60) }}
                  resizeMode="contain"
                  alt="Organization Logo"
                />
              ) : (
                <Icon as={MaterialCommunityIcons} name={icon.name} size="16" color="primary.50" />
              )}

              {/* Title */}
              <Text
                fontSize="32"
                fontFamily="serif"
                fontStyle="italic"
                textAlign="center"
                mt={4}
                color="function.50"
                lineHeight="32"
              >
                {title}
              </Text>

              {/* Subtitle */}
              <Text fontSize="16" fontFamily="body" color="function.30" textAlign="center" mb={4} lineHeight="20">
                {subtitle}
              </Text>

              {/* Options */}
              <VStack space={2} width="100%" maxWidth="600px" aria-label={title}>
                {options.map((option) => {
                  const isSelected = selectedOptions.includes(option.value);
                  return (
                    <Pressable
                      key={option.value}
                      onPress={() => toggleSelection(option.value)}
                      testID={option.testID || `selection-option-${option.value}`}
                      accessibilityRole="button"
                      accessibilityState={{ selected: isSelected }}
                      aria-selected={isSelected}
                      aria-label={option.label}
                    >
                      <Box
                        flexDirection="row"
                        alignItems="center"
                        p={4}
                        borderRadius={theme.borderRadii.card.default}
                        borderWidth={1}
                        borderColor={isSelected ? "primary.50" : "function.20"}
                        bg={isSelected ? "primary.20" : "primary.10"}
                        width="100%"
                        shadow={isSelected ? SELECTED_SHADOW : NO_SHADOW}
                      >
                        {/* Icon or Emoji */}
                        {option.icon ? getOptionIconComponent(option, isSelected) : null}

                        {/* Text Container */}
                        <VStack flex={1} ml={4}>
                          <Text
                            fontSize="16"
                            fontFamily="body"
                            fontWeight="500"
                            color={isSelected ? "primary.50" : "function.50"}
                          >
                            {option.label}
                          </Text>
                          <Text fontSize="14" fontFamily="body" color="function.30" lineHeight="20">
                            {option.description}
                          </Text>
                        </VStack>
                      </Box>
                    </Pressable>
                  );
                })}
              </VStack>

              {/* Error Message */}
              {errorMessage ? (
                <Text color="red.500" textAlign="center" mt={2}>
                  {errorMessage}
                </Text>
              ) : null}

              {/* Next Button */}
              {!nextButtonDisabled && (
                <Button
                  onPress={onPressNext}
                  borderRadius={theme.borderRadii.button.default}
                  bg="primary.50"
                  _text={{
                    fontFamily: "body",
                    fontWeight: "500",
                    fontSize: "16",
                    color: "primary.10",
                  }}
                  _pressed={{
                    bg: "primary.50",
                  }}
                  _disabled={{
                    bg: "function.20",
                    _text: {
                      color: "function.30",
                    },
                  }}
                  py={3}
                  px={8}
                  mt={4}
                  mb={6}
                  isLoading={submitButtonLoading}
                  isDisabled={nextButtonDisabled}
                  testID={nextButtonTestIdPostfix ? `next-button-${nextButtonTestIdPostfix}` : "next-button"}
                >
                  {t("general.next_button_text")}
                </Button>
              )}
            </VStack>
          </ScrollView>
        </VStack>
      </Box>
    </ScreenContainer>
  );
};

export default SelectionScreen;
